import { UserInfoDto, UserInfoDtoWithToken } from "@core/types";
import { isTokenExpired } from "@core/utils";
import { UserService } from "@services";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";

const queryKey = ["authToken"];

export const useCcauToken: (getToken: () => Promise<string | undefined>) => {
  userInfo: UserInfoDto | undefined;
  getCcauToken: () => Promise<string | undefined>;
  ccauAccessToken: string | undefined;
} = (getToken) => {
  const navigateTo = useNavigate();
  const queryClient = useQueryClient();
  const service = useMemo(
    () =>
      new UserService(
        { getToken, getCcauToken: () => Promise.resolve(undefined) },
        navigateTo,
      ),
    [getToken, navigateTo],
  );

  const queryFn = useCallback(async () => {
    const cachedData = queryClient.getQueryData<UserInfoDtoWithToken>(queryKey);

    if (!cachedData || isTokenExpired(cachedData?.accessToken)) {
      return service.getUserInfo();
    }

    return cachedData;
  }, [queryClient, service]);

  const { data, refetch } = useQuery({
    queryKey,
    queryFn,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });

  return {
    ccauAccessToken: data?.accessToken,
    userInfo: data || undefined,
    getCcauToken: async () => {
      const { accessToken } = data || {};
      if (!accessToken || isTokenExpired(accessToken)) {
        const res = await refetch();
        return res.data?.accessToken;
      }
      return data?.accessToken;
    },
  };
};
